import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { SpeakerHigh, SpeakerSlash, Play, ArrowsClockwise, Lightbulb, Plus, Check, Star, House } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { Bunny } from "./Bunny";
import { Apple } from "./Apple";
import { Basket } from "./Basket";
import { Confetti } from "./Confetti";
import { useNarration } from "./useNarration";
import { plantElement } from "../../home/worldStore";
import { sfx, unlockAudio, setSoundMuted } from "./sound";

const NUMBER_WORDS = ["zero", "one", "two", "three", "four", "five"];
// Narration is split into short phrases so the voice never gets cut off and
// each idea lands with a gentle pause before the next.
const LINES = {
  intro: ["Hi! I'm Bloom Bunny.", "Let's count some apples together!"],
  s1: ["Look!", "The bunny found two apples."],
  s2: ["Then the bunny found", "two more apples."],
  question: ["How many apples are there altogether?", "Tap your answer."],
  praise: ["Brilliant!", "Four apples!"],
  complete: ["You did it!", "Great counting!"],
  wrong: ["So close!", "Let's count together."],
  countTogether: ["Let's count together."],
  countDone: ["There are four apples.", "Can you find the four?"],
};

export function AdditionStory() {
  const reduce = useReducedMotion();
  const { speak, cancel } = useNarration();

  const [started, setStarted] = useState(false);
  const [phase, setPhase] = useState("start"); // start|s1|s2|question|counting|correct|complete
  const [appleCount, setAppleCount] = useState(0);
  const [countTo, setCountTo] = useState(0);
  const [selected, setSelected] = useState(null);
  const [progress, setProgress] = useState(20);
  const [mood, setMood] = useState("happy");
  const [muted, setMuted] = useState(false);
  const [caption, setCaption] = useState("");
  const [hintGlow, setHintGlow] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showXp, setShowXp] = useState(false);

  const timers = useRef([]);
  const lastLine = useRef("");
  const mutedRef = useRef(false);

  const after = useCallback((ms, fn) => {
    const id = setTimeout(fn, ms);
    timers.current.push(id);
    return id;
  }, []);
  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  useEffect(() => {
    mutedRef.current = muted;
    setSoundMuted(muted);
    if (muted) cancel();
  }, [muted, cancel]);

  useEffect(() => () => { clearTimers(); cancel(); }, [clearTimers, cancel]);

  const say = useCallback((text, opts) => {
    lastLine.current = text;
    setCaption(Array.isArray(text) ? text.join(" ") : text);
    if (!mutedRef.current) speak(text, opts);
  }, [speak]);

  const beginProblem = useCallback(() => {
    setPhase("s1");
    setMood("idle");
    setAppleCount(2);
    if (!mutedRef.current) { sfx.pop(); after(240, () => !mutedRef.current && sfx.pop()); }
    say(LINES.s1);
    after(3300, () => {
      setPhase("s2");
      setAppleCount(4);
      if (!mutedRef.current) { sfx.pop(); after(240, () => !mutedRef.current && sfx.pop()); }
      say(LINES.s2);
      after(3500, () => { setPhase("question"); setMood("idle"); say(LINES.question); });
    });
  }, [after, say]);

  const start = useCallback(() => {
    unlockAudio();
    setStarted(true);
    setMood("happy");
    say(LINES.intro);
    after(2900, beginProblem);
  }, [after, say, beginProblem]);

  const runCount = useCallback(() => {
    setPhase("counting");
    setSelected(null);
    setCountTo(0);
    for (let i = 1; i <= 4; i++) {
      after(i * 780, () => {
        setCountTo(i);
        if (!mutedRef.current) sfx.count();
        say(NUMBER_WORDS[i], { rate: 0.95 });
      });
    }
    after(4 * 780 + 850, () => {
      setHintGlow(true);
      setMood("happy");
      say(LINES.countDone);
      setPhase("question");
    });
  }, [after, say]);

  const complete = useCallback(() => {
    setPhase("complete");
    setMood("happy");
    if (!mutedRef.current) sfx.complete();
    say(LINES.complete);
  }, [say]);

  const win = useCallback(() => {
    clearTimers();
    setSelected(4);
    setPhase("correct");
    setMood("celebrating");
    setHintGlow(false);
    setProgress(100);
    setShowConfetti(!reduce);
    setShowXp(true);
    if (!mutedRef.current) sfx.success();
    say(LINES.praise);
    plantElement({ from: "addition", label: "You grew this by counting apples!" });
    after(2700, complete);
  }, [after, say, complete, clearTimers, reduce]);

  const wrongTry = useCallback(() => {
    clearTimers();
    setMood("thinking");
    if (!mutedRef.current) sfx.tryAgain();
    say(LINES.wrong);
    after(1700, runCount);
  }, [after, say, runCount, clearTimers]);

  const countTogether = useCallback(() => {
    clearTimers();
    setMood("thinking");
    say(LINES.countTogether);
    after(900, runCount);
  }, [after, say, runCount, clearTimers]);

  const handleAnswer = useCallback((n) => {
    if (phase !== "question") return;
    if (!mutedRef.current) sfx.tap();
    setSelected(n);
    if (n === 4) win();
    else wrongTry();
  }, [phase, win, wrongTry]);

  const replay = useCallback(() => {
    unlockAudio();
    say(lastLine.current || LINES.question);
  }, [say]);

  const playAgain = useCallback(() => {
    clearTimers();
    setShowConfetti(false);
    setShowXp(false);
    setHintGlow(false);
    setSelected(null);
    setCountTo(0);
    setAppleCount(0);
    setProgress(20);
    setMood("happy");
    after(150, beginProblem);
  }, [after, beginProblem, clearTimers]);

  /* ---------------------------------- derived ---------------------------------- */
  const flying = phase === "correct";
  const cardsVisible = ["question", "counting", "correct"].includes(phase);
  // Apples drift down and to the right, tumbling into the basket beside the bunny.
  const appleAnimate = (i) => (flying ? (reduce ? { opacity: 0 } : { x: 70 + i * 6, y: 150, scale: 0.12, opacity: 0 }) : { x: 0, y: 0, scale: 1, opacity: 1 });
  const appleTransition = (i) => (flying ? { duration: 0.55, delay: i * 0.1, ease: "easeIn" } : reduce ? { duration: 0.25 } : { type: "spring", stiffness: 240, damping: 15, delay: (i % 2) * 0.14 });

  return (
    <div className="relative flex h-[100svh] w-full flex-col overflow-hidden bg-gradient-to-b from-[#FFF7EF] via-[#FFF1E6] to-[#DCF3EC] dark:from-[#171C28] dark:via-[#1B2030] dark:to-[#142420]">
      {/* decorative sky */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute right-6 top-6 h-24 w-24 rounded-full bg-secondary/70 blur-[2px] dark:bg-secondary/40" />
        <div className="absolute right-4 top-4 h-28 w-28 rounded-full bg-secondary/30 blur-2xl" />
        {!reduce && (
          <>
            <motion.div className="absolute left-[12%] top-[12%] h-7 w-20 rounded-full bg-white/80 dark:bg-white/10" animate={{ x: [0, 18, 0] }} transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }} />
            <motion.div className="absolute left-[58%] top-[8%] h-6 w-16 rounded-full bg-white/70 dark:bg-white/10" animate={{ x: [0, -16, 0] }} transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }} />
          </>
        )}
        {/* meadow hill */}
        <div className="absolute -bottom-28 left-1/2 h-[55%] w-[170%] -translate-x-1/2 rounded-[50%] bg-gradient-to-b from-teal/30 to-teal/10 dark:from-teal/15 dark:to-transparent" />
      </div>

      {/* TOP BAR */}
      <div className="relative z-30 flex shrink-0 items-center justify-between px-4 py-2.5 sm:px-6">
        <div className="flex items-center gap-2">
          <Link to="/home/year1" aria-label="Back to your world" data-testid="exit-home" className="flex h-11 w-11 items-center justify-center rounded-full bg-card/80 text-foreground shadow-soft backdrop-blur transition hover:bg-card">
            <House size={20} weight="bold" />
          </Link>
          <span className="rounded-full bg-card/80 px-3 py-1.5 text-sm font-bold text-foreground shadow-soft backdrop-blur">Counting Apples</span>
        </div>
        <button
          onClick={() => setMuted((m) => !m)}
          aria-label={muted ? "Turn sound on" : "Turn sound off"}
          data-testid="mute-toggle"
          className="flex h-11 w-11 items-center justify-center rounded-full bg-card/80 text-foreground shadow-soft backdrop-blur transition hover:bg-card"
        >
          {muted ? <SpeakerSlash size={20} weight="bold" /> : <SpeakerHigh size={20} weight="fill" />}
        </button>
      </div>

      {/* PROGRESS */}
      <div className="relative z-30 shrink-0 px-4 sm:px-6">
        <div className="mx-auto flex max-w-xl items-center gap-3">
          <div className="h-3.5 flex-1 overflow-hidden rounded-full bg-card/70 shadow-inner backdrop-blur">
            <motion.div className="h-full rounded-full bg-gradient-to-r from-primary to-secondary" animate={{ width: `${progress}%` }} transition={{ type: "spring", stiffness: 120, damping: 20 }} />
          </div>
          <AnimatePresence>
            {showXp && (
              <motion.span initial={{ scale: 0, y: 6 }} animate={{ scale: 1, y: 0 }} className="inline-flex items-center gap-1 rounded-full bg-secondary/30 px-2.5 py-1 text-sm font-black text-secondary-ink shadow-soft">
                <Star size={14} weight="fill" /> +5
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* QUESTION / NARRATION BUBBLE — always visible once started */}
      <div className="relative z-30 mt-2 flex min-h-[52px] shrink-0 items-center justify-center px-4">
        <AnimatePresence mode="wait">
          {started && caption && (
            <motion.div
              key={caption}
              initial={{ opacity: 0, y: -8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0 }}
              className="flex max-w-[92%] items-center gap-2 rounded-full bg-card px-4 py-2 text-center shadow-soft"
            >
              <SpeakerHigh size={16} weight="fill" className="flex-shrink-0 text-primary" />
              <span data-testid="narration-caption" className="text-sm font-bold text-foreground sm:text-base">{caption}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* SCENE — apples up top, bunny + basket side by side on the meadow */}
      <div className="relative z-10 min-h-0 w-full flex-1">
        {showConfetti && <Confetti />}

        {/* apples */}
        <div className="absolute left-1/2 top-2 z-10 flex -translate-x-1/2 gap-3 sm:top-5 sm:gap-5">
          {[0, 1, 2, 3].map((i) =>
            i < appleCount ? (
              <motion.div
                key={i}
                className="relative"
                initial={reduce ? { opacity: 0 } : { scale: 0, y: -44, opacity: 0 }}
                animate={appleAnimate(i)}
                transition={appleTransition(i)}
              >
                {countTo > i && <span className="absolute inset-0 -z-10 rounded-full bg-secondary/30 blur-md" />}
                <div className={cn("transition-transform duration-300", countTo > i && "scale-110")}>
                  <Apple className="h-12 w-12 sm:h-14 sm:w-14" />
                </div>
                <AnimatePresence>
                  {countTo > i && (
                    <motion.span
                      initial={{ scale: 0, y: 6 }}
                      animate={{ scale: 1, y: 0 }}
                      className="absolute -top-3 left-1/2 flex h-7 w-7 -translate-x-1/2 items-center justify-center rounded-full bg-secondary font-display text-sm font-black text-secondary-ink shadow-soft"
                    >
                      {i + 1}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.div>
            ) : null,
          )}
        </div>

        {/* bunny + basket as one scene */}
        <div className="absolute inset-x-0 bottom-1 z-0 flex items-end justify-center gap-2 sm:gap-8">
          <motion.div
            animate={reduce ? {} : mood === "celebrating" ? { y: [0, -24, 0, -12, 0] } : { y: [0, -7, 0] }}
            transition={reduce ? {} : mood === "celebrating" ? { duration: 0.9, repeat: 2 } : { duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
          >
            <Bunny mood={mood} className="h-[136px] w-auto sm:h-[168px]" />
          </motion.div>
          <motion.div
            className="mb-1"
            animate={!reduce && phase === "correct" ? { scale: [1, 1.12, 1] } : { scale: 1 }}
            transition={{ duration: 0.5, repeat: phase === "correct" ? 3 : 0 }}
          >
            <Basket className="h-[88px] w-auto sm:h-[108px]" />
          </motion.div>
        </div>
      </div>

      {/* CONTROLS */}
      <div className="relative z-30 shrink-0 px-4 pb-4 pt-1 sm:pb-5">
        <div className="mx-auto w-full max-w-xl">
          {/* equation hint */}
          <AnimatePresence>
            {(phase === "question" || phase === "counting") && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-3 flex items-center justify-center gap-2 rounded-2xl bg-card/70 py-2 shadow-soft backdrop-blur sm:gap-3"
              >
                <span className="flex gap-0.5"><Apple className="h-6 w-6" /><Apple className="h-6 w-6" /></span>
                <Plus size={18} weight="bold" className="text-muted-foreground" />
                <span className="flex gap-0.5"><Apple className="h-6 w-6" /><Apple className="h-6 w-6" /></span>
                <span className="font-display text-xl font-black text-muted-foreground">=</span>
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 font-display text-lg font-black text-primary-strong">?</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* answer cards OR completion */}
          {phase === "complete" ? (
            <motion.div initial={{ opacity: 0, scale: 0.92, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} className="mx-auto max-w-md rounded-[1.75rem] border border-border bg-card/90 p-4 text-center shadow-soft-lg backdrop-blur">
              <div className="flex items-center justify-center gap-1">
                {[0, 1, 2].map((i) => (
                  <motion.span key={i} initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.1 + i * 0.12, type: "spring", stiffness: 400, damping: 12 }}>
                    <Star size={i === 1 ? 40 : 30} weight="fill" className="text-secondary" />
                  </motion.span>
                ))}
              </div>
              <p className="mt-1 font-display text-2xl font-black">You did it!</p>
              <p className="text-sm font-semibold text-muted-foreground">Great counting — four apples!</p>
              <button
                onClick={playAgain}
                data-testid="play-again"
                className="mt-3 inline-flex min-h-[52px] items-center gap-2 rounded-full bg-primary px-7 font-display text-lg font-extrabold text-primary-foreground shadow-glow transition-all duration-200 ease-bounce hover:-translate-y-0.5 active:scale-95"
              >
                <ArrowsClockwise size={20} weight="bold" /> Play again
              </button>
            </motion.div>
          ) : (
            <AnimatePresence>
              {cardsVisible && (
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }} className="flex items-end justify-center gap-3 sm:gap-5">
                  {[3, 4, 5].map((n) => {
                    const isCorrectPick = phase === "correct" && n === 4;
                    const glow = hintGlow && n === 4 && phase === "question";
                    return (
                      <motion.button
                        key={n}
                        onClick={() => handleAnswer(n)}
                        disabled={phase !== "question"}
                        aria-label={`Answer ${n}`}
                        data-testid={`answer-${n}`}
                        whileTap={{ scale: 0.93 }}
                        animate={glow && !reduce ? { scale: [1, 1.05, 1] } : { scale: 1 }}
                        transition={{ duration: 1.1, repeat: glow ? Infinity : 0 }}
                        className={cn(
                          "flex h-24 w-[4.75rem] flex-col items-center justify-center gap-1.5 rounded-[1.5rem] border-4 bg-card shadow-soft-lg transition-colors duration-200 disabled:cursor-default sm:h-28 sm:w-24",
                          isCorrectPick ? "border-teal bg-teal/15" : glow ? "border-secondary ring-8 ring-secondary/30" : "border-border",
                          phase === "question" && "hover:-translate-y-1 hover:border-primary",
                        )}
                      >
                        <span className={cn("font-display text-4xl font-black sm:text-5xl", isCorrectPick ? "text-teal-ink" : "text-foreground")}>
                          {isCorrectPick ? <Check size={42} weight="bold" /> : n}
                        </span>
                        <span className="flex gap-1">
                          {Array.from({ length: n }).map((_, d) => (
                            <span key={d} className={cn("h-1.5 w-1.5 rounded-full", isCorrectPick ? "bg-teal" : "bg-primary/60")} />
                          ))}
                        </span>
                      </motion.button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          )}

          {/* persistent audio + hint controls */}
          {started && phase !== "complete" && (
            <div className="mt-3 flex items-center justify-center gap-3">
              <button
                onClick={replay}
                data-testid="replay-button"
                className="inline-flex min-h-[52px] items-center gap-2 rounded-full bg-primary px-6 font-display text-base font-extrabold text-primary-foreground shadow-glow transition-all duration-200 ease-bounce hover:-translate-y-0.5 active:scale-95 sm:text-lg"
              >
                <SpeakerHigh size={22} weight="fill" /> Listen Again
              </button>
              <button
                onClick={countTogether}
                disabled={phase !== "question"}
                data-testid="hint-button"
                className="inline-flex min-h-[52px] items-center gap-2 rounded-full border-2 border-border bg-card px-5 font-display text-base font-bold text-foreground shadow-soft transition-all hover:border-primary hover:text-primary-strong active:scale-95 disabled:opacity-50"
              >
                <Lightbulb size={20} weight="fill" /> Hint
              </button>
            </div>
          )}
        </div>
      </div>

      {/* START SPLASH */}
      <AnimatePresence>
        {!started && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-b from-[#FFF7EF] via-[#FFF1E6] to-[#DCF3EC] px-6 text-center dark:from-[#171C28] dark:via-[#1B2030] dark:to-[#142420]"
          >
            <motion.div animate={reduce ? {} : { y: [0, -10, 0] }} transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}>
              <Bunny mood="happy" className="h-[180px] w-auto" />
            </motion.div>
            <h1 className="mt-4 font-display text-4xl font-black tracking-tight text-foreground">Counting Apples</h1>
            <p className="mt-2 max-w-xs font-semibold text-muted-foreground">Help Bloom Bunny count the apples in the basket!</p>
            <button
              onClick={start}
              data-testid="start-button"
              className="mt-7 inline-flex min-h-[64px] items-center gap-3 rounded-full bg-primary px-10 font-display text-xl font-black text-primary-foreground shadow-glow transition-all duration-200 ease-bounce hover:-translate-y-1 active:scale-95"
            >
              <Play size={26} weight="fill" /> Tap to Play
            </button>
            <p className="mt-4 flex items-center gap-1.5 text-sm font-bold text-muted-foreground">
              <SpeakerHigh size={16} weight="fill" /> Sound on for the best experience
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default AdditionStory;
